import React from 'react';
import { Button as ChakraButton, ButtonProps as ChakraButtonProps } from '@chakra-ui/react';

export type ButtonType =
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'danger'
    | 'danger-light'
    | 'ghost'
    | 'info'
    | 'blue'
    | 'green'
    | 'gray'
    | 'red'
    | 'white';

export interface ButtonProps extends ChakraButtonProps {
    buttonType?: ButtonType;
    children: React.ReactNode;
    isLoading?: boolean;
}

const getButtonStyles = (buttonType: ButtonType) => {
    switch (buttonType) {
        case 'primary':
            return {
                bg: 'blue.100',
                color: 'blue.700',
                border: '1px solid',
                borderColor: 'blue.300',
                _hover: { bg: 'blue.200' },
                _active: { bg: 'blue.300' },
            };
        case 'secondary':
            return {
                bg: 'gray.100',
                color: 'gray.700',
                border: '1px solid',
                borderColor: 'gray.300',
                _hover: { bg: 'gray.200' },
                _active: { bg: 'gray.300' },
            };
        case 'success':
            return {
                bg: 'yellow.600',
                color: 'black',
                _hover: { bg: 'yellow.700' },
                _active: { bg: 'yellow.700' },
                _focus: { boxShadow: '0 0 0 3px rgba(236, 201, 75, 0.6)' },
            };
        case 'warning':
            return {
                bg: 'orange.50',
                color: 'orange.600',
                _hover: { bg: 'orange.100' },
                _active: { bg: 'orange.200' },
                _focus: { boxShadow: '0 0 0 3px rgba(237, 137, 54, 0.6)' },
            };
        case 'danger':
            return {
                bg: 'black',
                color: 'white',
                _hover: { bg: 'gray.500' },
                _active: { bg: 'gray.500' },
            };
        case 'danger-light':
            return {
                bg: 'red.100',
                color: 'red.700',
                border: '1px solid',
                borderColor: 'red.300',
                _hover: { bg: 'red.200' },
                _active: { bg: 'red.300' },
            };
        case 'info':
            return {
                bg: 'blue.50',
                color: 'blue.600',
                _hover: { bg: 'blue.100' },
                _active: { bg: 'blue.200' },
                _focus: { boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)' },
            };
        case 'ghost':
            return {
                bg: 'transparent',
                color: 'gray.600',
                _hover: { bg: 'gray.100' },
                _active: { bg: 'gray.200' },
                _focus: { boxShadow: '0 0 0 3px rgba(160, 174, 192, 0.6)' },
            };
        case 'gray':
            return {
                bg: 'gray.100',
                color: 'gray.600',
                _hover: { bg: 'gray.200' },
                _active: { bg: 'gray.400' },
                _focus: { boxShadow: '0 0 0 3px rgba(160, 174, 192, 0.6)' },
            };
        default:
            return {
                bg: 'blue.50',
                color: 'blue.600',
                _hover: { bg: 'blue.100' },
                _active: { bg: 'blue.200' },
                _focus: { boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)' },
            };
    }
};

const Button: React.FC<ButtonProps> = ({
    buttonType = 'primary',
    children,
    isLoading,
    disabled,
    ...props
}) => {
    const buttonStyles = getButtonStyles(buttonType);

    return (
        <ChakraButton
            {...buttonStyles}
            loading={isLoading}
            disabled={disabled || isLoading}
            borderRadius={0}
            fontWeight={400}
            fontSize="md"
            {...props}
        >
            {children}
        </ChakraButton>
    );
};

export default Button;
